<script lang="ts" setup>
import { IEvent } from "~/types/sports/event";
import { IMarket } from "~/types/sports/market";
import { IOutcome } from "~/types/sports/outcome";
import { IPrice } from "~/types/sports/price";
import SuperBoostMarket from "./SuperBoostMarket.vue";
import { IScore } from "~/types/sports/score";
import { signalREndpoints, useSignalR } from "~/composables/SignalR/useSignalR";
import { useSettings } from "~/store/settings";

interface IProps {
	boostedEvent: { markets: IMarket[]; outcomes: IOutcome[]; prices: IPrice[]; sportEvent: IEvent };
}

const props = defineProps<IProps>();

const settings = useSettings();

const event = ref(props.boostedEvent.sportEvent);
const marketsMap = ref<Map<string, IMarket>>(new Map());
const outcomesMap = ref<Map<string, IOutcome>>(new Map());
const pricesMap = ref<Map<string, IPrice>>(new Map());

for (const market of props.boostedEvent.markets) {
	marketsMap.value.set(market.marketId, market as IMarket);
}
for (const outcome of props.boostedEvent.outcomes) {
	if (outcome.marketId === props.boostedEvent.markets[0].marketId) outcomesMap.value.set(outcome.outcomeId, outcome as IOutcome);
}
for (const price of props.boostedEvent.prices) {
	pricesMap.value.set(price.outcomeId, price as IPrice);
}

const filteredOutcomes = computed(() => props.boostedEvent.outcomes.filter((o) => o.marketId === props.boostedEvent.markets[0].marketId));

// Signal R
const superBoostsMarketsCallback = (listener: string, data: IEvent | IMarket | IOutcome | IPrice | IScore) => {
	switch (listener) {
		case "event":
			if (event.value.eventId === data.eventId) event.value = data as IEvent;
			break;
		case "market":
			if ("marketId" in data) {
				marketsMap.value.set(data.marketId, data as IMarket);
			}
			break;
		case "outcome":
			if ("outcomeId" in data) {
				outcomesMap.value.set(data.outcomeId, data as IOutcome);
			}
			break;
		case "price":
			if ("outcomeId" in data) {
				pricesMap.value.set(data.outcomeId, data as IPrice);
			}
			break;
	}
};

const betbookSignalR = useSignalR("superBoosts", superBoostsMarketsCallback);

onBeforeUnmount(() => {
	betbookSignalR.unSubscribe(signalREndpoints.unsubscriptionBetbookEndpoint, {
		eventIds: [props.boostedEvent.sportEvent.eventId],
		marketTypes: props.boostedEvent.markets.map((m) => m.name),
		countryCode: settings.getCountryConfig?.countryIsoTwo,
	});
});

onMounted(() => {
	betbookSignalR.subscribe(signalREndpoints.subscriptionBetbookEndpoint, [props.boostedEvent.sportEvent.eventId], {
		eventIds: [props.boostedEvent.sportEvent.eventId],
		marketTypes: props.boostedEvent.markets.map((m) => m.name),
		countryCode: settings.getCountryConfig?.countryIsoTwo,
	});
});
</script>

<template>
	<div>
		<strong class="text-lg text-dark-800">{{ event.name }}</strong>
		<SuperBoostMarket
			v-for="market of boostedEvent.markets"
			:key="market.marketId"
			:event="event"
			:market="marketsMap.get(market.marketId)"
			:outcomes="filteredOutcomes"
			:outcomesMap="outcomesMap"
			:prices="boostedEvent.prices"
			:pricesMap="pricesMap"
		/>
	</div>
</template>
